export default function PrivacyPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-neutral-900 text-white py-6">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="p-1 rounded-lg">
                  <img src="/icon.svg" alt="AIBoard" className="h-8 w-8" />
                </div>
                <span className="ml-3 text-xl font-bold text-white">AIBoard</span>
              </div>
              <h1 className="text-2xl font-bold">Privacy Policy</h1>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="prose prose-lg max-w-none">
          <div className="bg-neutral-50 p-6 rounded-lg mb-8">
            <p className="text-sm text-neutral-600 mb-2">Last updated: {new Date().toLocaleDateString()}</p>
            <p className="text-neutral-700">
              Your privacy is important to AIBoard. Please read the details in this policy carefully, as it applies to the interactions AIBoard has with you and the services we provide.
            </p>
          </div>

          <section className="mb-8">
            <h2 className="text-2xl font-bold text-neutral-900 mb-4">Privacy Policy</h2>
            
            <p className="mb-4">
              This privacy policy ("Policy") explains how AIBoard and our respective affiliates (collectively, "AIBoard", "we", "us" or "our") collects, stores, uses, and discloses personal data about you, how we use it, how and when it may be shared, the rights and choices you have with respect to your personal data, how we communicate with you and how you can make requests or submit inquiries to us about your personal data.
            </p>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
              <h3 className="text-lg font-semibold mb-2">Contents</h3>
              <ul className="text-sm space-y-1">
                <li>• What Does This Policy Cover</li>
                <li>• How Is This Policy Updated</li>
                <li>• Information We Collect</li>
                <li>• How We Use Your Personal Data</li>
                <li>• Disclosure of Personal Data</li>
                <li>• How We Protect Your Personal Data</li>
                <li>• Cookies and Other Information Collection Tools</li>
                <li>• Retention of Personal Data</li>
                <li>• Your Privacy Rights and Choices</li>
                <li>• Children's Privacy</li>
                <li>• International Data Transfers</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">What Does This Policy Cover</h2>
            <p className="mb-4">
              This Policy applies to personal data we collect, including personal data we collect through our interactions (collectively the "Sites") in connection with products and services we provide you (and together with the Sites, the "Services"), and any other interactions you may have with us.
            </p>
            <p className="mb-4">
              In this Policy, "personal data" means any information that identifies, relates to, describes, is reasonably capable of being associated with or reasonably can be used to identify an individual or household and other data that is linked to personal data or is otherwise defined as "personal data," "personal information," or "personally identifiable information" by applicable laws.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">How Is This Policy Updated</h2>
            <p className="mb-4">
              We may modify this Policy from time to time. If we make changes, we will revise the "Last Updated" date at the top of the Policy on our Sites. We encourage you to review this Policy each time you visit our Sites to stay informed of our privacy practices and the choices available to you.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Information We Collect</h2>
            <p className="mb-4">
              The personal data we collect about you depends on how you interact with us or use our Services. Below, we describe the categories of personal data we collect and the sources of this information.
            </p>

            <h3 className="text-lg font-semibold mb-3">Information You Provide to Us</h3>
            <p className="mb-2">We collect personal data from you when you use our Services, send us an email, fill out a form, subscribe to our newsletter, respond to a survey, or otherwise communicate with us.</p>
            <p className="mb-2">Types of personal data we may collect include:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li>Contact information such as your name, email address, telephone number, and mailing address</li>
              <li>Registration Information such as username and credentials, professional or employment related information, and account details</li>
              <li>Payment Information such as your credit card or bank account number, which we may share with third-party payment processors</li>
              <li>Communications with us including through our online chat service, feedback, and survey responses</li>
              <li>Content and files such as documents, or other files you upload to our Service</li>
              <li>Any additional Information that you choose to provide</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">Information Collected Automatically</h3>
            <p className="mb-2">We and our service providers may automatically collect and/or log personal data about you, your computer or mobile device, and your interaction over time with the Services, such as:</p>
            <ul className="list-disc pl-6 space-y-1 mb-4">
              <li><strong>Device information:</strong> Internet Protocol ("IP") addresses, browser type, domain names, access times, device type, unique device identifiers, and other technical information</li>
              <li><strong>Transaction Information:</strong> Information when you conduct transactions through our Sites or otherwise purchase Services from us</li>
              <li><strong>Usage information:</strong> Information about your use of the Services, such as the pages you viewed, the Services you used or interacted with</li>
              <li><strong>Location Information:</strong> Information about the location of your device when you use our Services (with your consent)</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">Information We Collect From Other Sources</h3>
            <p className="mb-4">
              We may also collect personal data about you from other sources, including publicly available databases and third parties, and combine that with information we collect through the Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">How We Use Your Personal Data</h2>
            <p className="mb-2">Depending on how you interact with us and the Services you utilize, we use your personal data to:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Provide and Improve Products and Services:</strong> Provide, activate, operate, maintain, develop, enhance, improve, and manage our Services and your accounts</li>
              <li><strong>Respond to You:</strong> Provide technical, product and other support and respond to your requests, inquiries, comments and concerns</li>
              <li><strong>Service Communications:</strong> Notify you about changes, updates and other announcements related to our Services</li>
              <li><strong>Personalize Your Experience:</strong> Customize our product offerings and present you with tailored content</li>
              <li><strong>Safety and Security:</strong> Detect and protect against fraud or illegal activities, security incidents, and harm to rights, property or safety</li>
              <li><strong>Research and Development:</strong> Conduct research and development, compile analytics, develop new products and services</li>
              <li><strong>Comply with Legal Obligations:</strong> Comply with our legal and financial obligations, resolve disputes, enforce our agreements</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Disclosure of Personal Data</h2>
            <p className="mb-2">We may disclose your personal data as follows:</p>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Affiliates and Subsidiaries:</strong> We disclose your personal data to our affiliates and other organizations under common control</li>
              <li><strong>Vendors and Service Providers:</strong> We disclose your personal data to our vendors and service providers to perform functions</li>
              <li><strong>Corporate Transactions:</strong> We disclose your personal data as part of any merger, sale of our assets, or acquisition</li>
              <li><strong>Analytics Providers:</strong> We disclose personal data to our analytics partners</li>
              <li><strong>Professional Advisors:</strong> We disclose your personal data to our lawyers, accountants, and other professional advisors</li>
              <li><strong>Security:</strong> We may disclose personal information to prevent, detect, and investigate potential security issues or fraudulent activity</li>
              <li><strong>Law Enforcement:</strong> We disclose your personal data when required to comply with applicable law or legal process</li>
              <li><strong>With your Consent:</strong> We disclose personal data with your consent or at your direction</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">How We Protect Your Personal Data</h2>
            <p className="mb-4">
              We implement commercially reasonable physical, technical and procedural measures to help protect personal data from unauthorized access or use; however, the transmission of information via the internet is not completely secure or private.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Cookies and Other Information Collection Tools</h2>
            
            <h3 className="text-lg font-semibold mb-3">What are cookies?</h3>
            <p className="mb-4">
              A cookie is a small file containing a string of characters that is sent to your computer when you visit a website or use an online service. When you visit the website again, the cookie allows that website to recognize your browser or device.
            </p>

            <h3 className="text-lg font-semibold mb-3">The type of cookies we may use include:</h3>
            <ul className="list-disc pl-6 space-y-2 mb-4">
              <li><strong>Necessary Cookies:</strong> Strictly necessary to make our Services function properly</li>
              <li><strong>Function Cookies:</strong> Help perform certain functionalities like sharing content on social media platforms</li>
              <li><strong>Performance Cookies:</strong> Used to understand and analyze key performance indexes of the Sites</li>
              <li><strong>Analytics Cookies:</strong> Used to understand how visitors interact with the Sites</li>
              <li><strong>Advertisement Cookies:</strong> Used to provide visitors with relevant ads and marketing campaigns</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">Why do we use them?</h3>
            <p className="mb-4">
              We use cookies to collect information about you so we can provide the experiences you request, recognize your visit, track your interactions, improve your experience and detect and prevent fraud.
            </p>

            <h3 className="text-lg font-semibold mb-3">How do I control cookies?</h3>
            <p className="mb-4">
              Web browsers allow some control of most cookies through the browser settings. Although users are not required to accept cookies, blocking or rejecting them may prevent access to some features available through the Services.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Retention of Personal Data</h2>
            <p className="mb-4">
              We will retain your personal data for as long as is necessary to fulfil the purpose for which this data was collected and for the business purposes explained in this Policy. We may retain your personal data to comply with our legal and regulatory obligations, protect against fraudulent or abusive activity, or exercise, establish or defend legal claims.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Your Privacy Rights and Choices</h2>
            <p className="mb-4">
              Consistent with applicable law, you may exercise certain rights regarding your personal data. These rights may vary depending on your country or state of residence.
            </p>

            <h3 className="text-lg font-semibold mb-3">Access, Correct or Delete your Personal Data</h3>
            <p className="mb-4">
              You may have the right to request access to and receive details about the personal data we maintain about you, correct inaccuracies, or request that we delete your personal data.
            </p>

            <h3 className="text-lg font-semibold mb-3">Opt Out of Direct Marketing</h3>
            <p className="mb-4">
              If you would like to opt out of receiving direct marketing mail or promotional emails from us, you can contact us through our support channels.
            </p>

            <h3 className="text-lg font-semibold mb-3">Withdraw Consent</h3>
            <p className="mb-4">
              Where you have provided your consent for us to use your personal data, you have the right to withdraw your consent and we will apply that preference going forward.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Children's Privacy</h2>
            <p className="mb-4">
              AIBoard is a business tool intended for use by businesses and organizations and not for consumer purposes. Our Services are not aimed at children under the age of 18. We do not knowingly collect information from children under the age of 18.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">International Data Transfers</h2>
            <p className="mb-4">
              We may process and store your personal data in various countries which may have different data protection laws than the region in which you are situated. In certain situations, we may be required to disclose personal data in response to lawful requests from officials.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-xl font-bold text-neutral-900 mb-4">Contact Information</h2>
            <p className="mb-4">
              If you have any questions about this Privacy Policy or if you wish to exercise your privacy rights as outlined above, please contact us through our website or support channels.
            </p>
          </section>

          <div className="bg-neutral-50 p-6 rounded-lg mt-8">
            <h3 className="text-lg font-semibold mb-2">Additional Information for Residents of Certain U.S. States</h3>
            <p className="text-sm text-neutral-600 mb-4">
              If you are a resident of California, Colorado, Connecticut, Montana, Oregon, Texas, Utah and Virginia, you may have certain rights and protections under applicable state privacy laws regarding the processing of your personal data.
            </p>
            
            <h3 className="text-lg font-semibold mb-2">Your Additional Privacy Rights</h3>
            <p className="text-sm text-neutral-600">
              Under certain circumstances, you may have additional rights including the right to opt out of sales or sharing of personal data, the right to request information about data practices, and the right not to be discriminated against for exercising these rights.
            </p>
          </div>
        </div>
      </main>
    </div>
  )
} 